import type { ReactElement } from 'react'
import type { RadioProps } from './Radio.types'

/**
 * radio — controlled single-choice primitive.
 *
 * THE SHAPE OF A CONTROL IS A PROMISE ABOUT WHAT IT DOES. A square ticks
 * independently of its neighbours; a circle replaces them. ⛔ Single choice is a
 * radio everywhere in this portal, multi-select is a checkbox, and neither
 * borrows the other's shape.
 *
 * It is the NATIVE input, unlike its checkbox neighbour: the browser already
 * owns the whole behaviour of a radio group — one tab stop, arrow keys walking
 * the choices, the group name binding them — and none of that is worth
 * re-implementing on top of a library. `appearance-none` drops only the
 * platform's drawing, so the tokens below are the entire visual: an outlined
 * ring that becomes a filled accent ring with the surface showing through as
 * the dot.
 */
export function Radio({
  checked,
  onSelect,
  name,
  disabled = false,
  'aria-label': ariaLabel,
}: RadioProps): ReactElement {
  return (
    <input
      type="radio"
      checked={checked}
      name={name}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={() => {
        onSelect()
      }}
      className="size-4 shrink-0 cursor-pointer appearance-none rounded-full border border-border-control bg-surface outline-offset-2 checked:border-[5px] checked:border-accent focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50"
    />
  )
}
